'use client';

import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { cva } from 'class-variance-authority';
import { useRemoveScrollLock } from '@/shared/hooks/dom/useRemoveScrollLock';
import { cn } from '@/shared/lib/classnames/cn';
import type { VariantProps } from 'class-variance-authority';
const dialogVariants = cva('bg-bgPrimary px-4 py-6 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] overflow-auto', {
  variants: {
    variant: {
      default: 'fixed inset-0 md:inset-auto md:left-1/2 md:top-1/2 md:-translate-x-1/2 md:-translate-y-1/2 z-[9999]',
      modal: 'fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-3xl w-[calc(100%-32px)] z-[9999]',
      full: 'fixed inset-0 !rounded-none md:!rounded-2xl z-[9999]',
      inFrame: 'fixed inset-auto left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[9999]'
    }
  },
  defaultVariants: {
    variant: 'default'
  }
});
const Dialog = DialogPrimitive.Root;
const DialogTrigger = DialogPrimitive.Trigger;
const DialogClose = DialogPrimitive.Close;
const DialogOverlay = React.forwardRef<React.ElementRef<typeof DialogPrimitive.Overlay>, React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>>(({
  className,
  ...props
}, ref) => <DialogPrimitive.Overlay ref={ref} className={cn('fixed inset-0 z-[9998] bg-black/50 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0', className)} {...props} />);
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;
interface DialogContentProps extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>, VariantProps<typeof dialogVariants> {
  overlayClick?: (event: React.MouseEvent<HTMLElement>) => void;
  removeScrollLock?: boolean;
}
const DialogContent = React.forwardRef<React.ElementRef<typeof DialogPrimitive.Content>, DialogContentProps>(({
  className,
  overlayClick,
  variant,
  removeScrollLock = false,
  children,
  ...props
}, ref) => {
  useRemoveScrollLock(removeScrollLock);
  return <DialogPrimitive.Portal>
        <div className="fixed inset-0 isolate z-[9997]">
          <DialogOverlay onClick={overlayClick} />
          <DialogPrimitive.Content ref={ref} className={cn(dialogVariants({
        variant,
        className
      }))} onCloseAutoFocus={e => {
        e.preventDefault();
      }} {...props}>
            {children}
          </DialogPrimitive.Content>
        </div>
      </DialogPrimitive.Portal>;
});
DialogContent.displayName = DialogPrimitive.Content.displayName;
const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => <div className={cn('mb-4 flex items-center justify-between', className)} {...props} data-sentry-component="DialogHeader" data-sentry-source-file="dialog.tsx" />;
DialogHeader.displayName = 'DialogHeader';
const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => <div className={cn('', className)} {...props} data-sentry-component="DialogFooter" data-sentry-source-file="dialog.tsx" />;
DialogFooter.displayName = 'DialogFooter';
const DialogTitle = React.forwardRef<React.ElementRef<typeof DialogPrimitive.Title>, React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>>(({
  className,
  ...props
}, ref) => <DialogPrimitive.Title ref={ref} className={cn('flex w-full items-center justify-between', className)} {...props} />);
DialogTitle.displayName = DialogPrimitive.Title.displayName;
const DialogDescription = React.forwardRef<React.ElementRef<typeof DialogPrimitive.Description>, React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>>(({
  className,
  ...props
}, ref) => <DialogPrimitive.Description ref={ref} className={cn('text-sm', className)} {...props} />);
DialogDescription.displayName = DialogPrimitive.Description.displayName;
export { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger };